import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import cx from "classnames";

import { SrOnly } from "../../../../../../components";

import { DataTableEditableCellEditIcon } from "./DataTableEditableCellEditIcon";
import { TableBodyCell } from "../../../base/cells/TableBodyCell";

import styles from "./DataTableEditableCells.module.scss";
import dataTableCellStyles from "../cells/DataTableCells.module.scss";
import { formatCellPositionString } from "../util/cellPositionHelpers";
import { EditableCellImperativeHandle, BaseEditableCellProps } from "./types";
import { useDTFocusDispatchContext } from "../context/focus/useDTFocusDispatchContext";
import {
  getColumnId,
  getCellValue,
  getCellOnChange,
  logWarningForInvalidCellValueType,
} from "../util/cellTypeHelpers";
import { DataTableBodyImmutableCell } from "../cells/DataTableBodyImmutableCell";
import { CellFocusContext } from "../cells/CellFocusContext";

export type DataTableEditableTextCellProps<T> = BaseEditableCellProps<T>;

/**
 * Renders an editable text cell in a DataTable. This component provides a text input that appears
 * when the cell is activated, allowing users to edit the cell value. Supports keyboard navigation
 * and commit/cancel operations.
 *
 * @param props - The props for the DataTableEditableTextCell component
 * @param props.cell - The TanStack table cell instance
 * @param props.cellProps - Props for styling and event handling of the cell container
 * @param props.cellPosition - The position of the cell in the table
 * @param props.children - The content to display when not editing
 * @param props.isHovered - Whether the cell is currently hovered
 * @param ref - Ref to the imperative handle for programmatic control
 * @returns The rendered editable text cell component
 */
function DataTableEditableTextCellInner<T>(
  props: DataTableEditableTextCellProps<T>,
  ref: React.Ref<EditableCellImperativeHandle>,
) {
  const { cell, cellProps, cellPosition, children } = props;
  const { onKeyDown: onCellKeyDown } = cellProps;
  const inputRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLTableCellElement>(null);
  const rowId = cell.row.id;
  const [isEditing, setIsEditing] = useState(false);

  // Use type-safe helpers to get column ID and value
  const columnId = getColumnId(cell);
  const cellValue = getCellValue(cell, columnId);

  const [initialValue, isValidValueType] = useMemo(() => {
    if (cellValue === undefined || cellValue === null) {
      return ["", true];
    }

    if (typeof cellValue === "string") {
      return [cellValue, true];
    }

    logWarningForInvalidCellValueType({
      columnId: String(columnId),
      editMode: "text",
      expectedType: "string",
      receivedType: Array.isArray(cellValue) ? "array" : typeof cellValue,
    });
    return ["", false];
  }, [cellValue, columnId]);

  const [draftValue, setDraftValue] = useState(initialValue);
  const [tabIndex, setTabIndex] = useState(-1);
  const { moveFocusToCell } = useDTFocusDispatchContext();

  useImperativeHandle(ref, () => ({
    invoke: beginEditing,
    focus: () => {
      cellRef.current?.focus();
      setTabIndex(0);
    },
  }));

  const skipCommitOnBlur = useRef(false);

  const onChange = getCellOnChange(cell, columnId);

  const commitValue = useCallback(() => {
    if (draftValue !== initialValue) {
      // This assertion is necessary because draftValue is string but onChange expects T[K]
      onChange?.(draftValue as T[typeof columnId], rowId);
    }
    setIsEditing(false);
  }, [rowId, draftValue, onChange, initialValue]);

  const resetValue = useCallback(() => {
    setDraftValue(initialValue);
    skipCommitOnBlur.current = true;
    cellRef.current?.focus();
    setIsEditing(false);
  }, [initialValue]);

  const beginEditing = useCallback(() => {
    setIsEditing(true);
  }, [setIsEditing]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDraftValue(e.target.value);
    },
    [setDraftValue],
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" || event.key === "F2") {
        event.preventDefault();
        skipCommitOnBlur.current = true;
        cellRef.current?.focus();
        commitValue();
        return;
      } else if (event.key === "Escape") {
        event.preventDefault();
        resetValue();
        return;
      }
    },
    [commitValue, resetValue],
  );

  const handleInputBlur = useCallback(() => {
    if (skipCommitOnBlur.current) {
      skipCommitOnBlur.current = false;
      return;
    }
    commitValue();
  }, [commitValue]);

  const classes = cx(
    styles["data-table-body-cell-editable"],
    cellProps.className,
    {
      [dataTableCellStyles["data-table-body-cell-editing"]]: isEditing,
    },
  );

  const handleCellKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTableCellElement>) => {
      if (isEditing) {
        return;
      }
      onCellKeyDown?.(event);
    },
    [onCellKeyDown, isEditing],
  );

  const handleCellFocus = useCallback(
    (event: React.FocusEvent<HTMLTableCellElement>) => {
      if (event.target !== cellRef.current) {
        // Skip for interactions within the cell (e.g. clicking on the input)
        return;
      }
      event.preventDefault();
      setTabIndex(0);
    },
    [setTabIndex],
  );

  const handleCellBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (cellRef.current?.contains(e.relatedTarget as Node)) {
        // Skip for interactions within the cell (e.g. clicking on the input)
        return;
      }
      setTabIndex(-1);
    },
    [setTabIndex, cellRef],
  );

  const handleCellMouseDown = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>) => {
      e.preventDefault();
      e.stopPropagation();
      beginEditing();
      moveFocusToCell(cellPosition);
    },
    [beginEditing, cellPosition, moveFocusToCell],
  );

  // Provide focus context to children (e.g., CellStatus) for keyboard accessibility.
  // Show tooltip when cell has focus but is not in editing mode.
  const cellFocusContextValue = useMemo(
    () => ({ isCellFocused: tabIndex === 0 && !isEditing }),
    [tabIndex, isEditing],
  );

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  if (!isValidValueType) {
    // Exclude cellProps to avoid passing it to the DOM
    const { cellProps: _cellProps, ...immutableCellProps } = props;
    return <DataTableBodyImmutableCell {...immutableCellProps} />;
  }

  return (
    <TableBodyCell
      ref={cellRef}
      type="data-table"
      className={classes}
      style={cellProps.style}
      onMouseDown={handleCellMouseDown}
      onKeyDown={handleCellKeyDown}
      tabIndex={tabIndex}
      onFocus={handleCellFocus}
      onBlur={handleCellBlur}
      data-cell-pos={formatCellPositionString(cellPosition)}
    >
      {isEditing ? (
        <>
          <input
            ref={inputRef}
            type="text"
            name={`${cell.column.id}-row-${cellPosition.rowIndex}`}
            value={draftValue}
            className={styles["data-table-body-cell-input"]}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
          />
        </>
      ) : (
        <CellFocusContext.Provider value={cellFocusContextValue}>
          {children}
          {!isEditing && <DataTableEditableCellEditIcon />}
        </CellFocusContext.Provider>
      )}
      <SrOnly>
        {isEditing
          ? "Press Enter or F2 to save cell value."
          : "Press Enter or F2 to edit cell value. Press Escape to cancel."}
      </SrOnly>
    </TableBodyCell>
  );
}

export const DataTableEditableTextCell = Object.assign(
  forwardRef(DataTableEditableTextCellInner) as <T>(
    props: DataTableEditableTextCellProps<T> & {
      ref?: React.Ref<EditableCellImperativeHandle>;
    },
  ) => React.ReactElement,
  { displayName: "DataTableEditableTextCell" },
);
